import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import { usePermissions } from 'hooks/usePermissions';
import { useQueryFilters } from 'hooks/useQueryFilters';
import { useRecordModal } from 'hooks/useRecordModal';
import { Identifier, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import { WhiteIpAddressesTooltip } from 'shared/mui/Tooltip/WhiteIpAddressesTooltip/WhiteIpAddressesTooltip';
import { Typography } from 'shared/mui/Typography';
import { DatagridWithStickyActions } from 'shared/react-admin/Datagrids/DatagridWithStickyActions/DatagridWithStickyActions';
import { NumberField, TextField } from 'shared/react-admin/Fields';
import { ListPage } from 'shared/react-admin/Pages';
import { Permission } from 'types/embedded/permission';
import { ProviderWhiteIpAddress } from 'types/embedded/provider';

import { ProvidersShowWhiteIpAddressesCreateDialog } from './ProvidersShowWhiteIpAddressesCreateDialog/ProvidersShowWhiteIpAddressesCreateDialog';
import { ProvidersShowWhiteIpAddressesDeleteDialog } from './ProvidersShowWhiteIpAddressesDeleteDialog/ProvidersShowWhiteIpAddressesDeleteDialog';
import { ProvidersShowWhiteIpAddressesEditDialog } from './ProvidersShowWhiteIpAddressesEditDialog/ProvidersShowWhiteIpAddressesEditDialog';
import { ProvidersShowWhiteIpAddressesShowDialog } from './ProvidersShowWhiteIpAddressesShowDialog/ProvidersShowWhiteIpAddressesShowDialog';

type Props = {
  providerId?: Identifier;
};

export const ProvidersShowWhiteIpAddresses: React.FC<Props> = ({ providerId }) => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const { queryFilterObject } = useQueryFilters();

  const [openCreateDialog, setOpenCreateDialog] = useState(!!queryFilterObject?.create);

  const {
    record: recordShowDialog,
    handleCloseRecordModal: handleCloseShowDialog,
    openRecordModal: openShowDialog,
    handleOpenRecordModal: handleOpenShowDialog,
  } = useRecordModal<ProviderWhiteIpAddress>();

  const {
    record: recordEditDialog,
    handleCloseRecordModal: handleCloseEditDialog,
    openRecordModal: openEditDialog,
    handleOpenRecordModal: handleOpenEditDialog,
  } = useRecordModal<ProviderWhiteIpAddress>();

  const {
    record: recordDeleteDialog,
    handleCloseRecordModal: handleCloseDeleteDialog,
    openRecordModal: openDeleteDialog,
    handleOpenRecordModal: handleOpenDeleteDialog,
  } = useRecordModal<ProviderWhiteIpAddress>();

  return (
    <ListPage
      actions={[
        hasResourcePermissions('provider_white_ip_addresses', [
          Permission.Full,
          Permission.Create,
        ]) && (
          <Button
            key="create"
            label="Добавить"
            onClick={() => setOpenCreateDialog(true)}
            startIcon={<ContentAdd />}
            sx={{ marginTop: '8px' }}
            variant="contained"
          />
        ),
      ]}
      empty={false}
      filter={{ provider: providerId }}
      headerTitle={{
        titleText: 'Белые IP-адреса',
        titleSize: 'smallBold',
      }}
      resource="provider_white_ip_addresses"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      <WithListContext
        render={({ data, isLoading }) => {
          const showAlert = !data?.length && !isLoading;
          if (showAlert) {
            return (
              <Alert severity="info">
                Белые IP адреса еще не были добавлены. Чтобы добавить белый IP адрес, нажмите кнопку
                &#34;Добавить&#34;
              </Alert>
            );
          }
          return (
            <>
              <DatagridWithStickyActions bulkActionButtons={false}>
                <TextField label="ID" sortBy="id" source="originId" />
                <TextField label="IP" source="ip" />
                <NumberField
                  emptyText={EMPTY_FIELD}
                  label={
                    <Box alignItems="center" display="flex" gap="8px">
                      <Typography fontSize="14px" fontWeight={500}>
                        Маска
                      </Typography>
                      <WhiteIpAddressesTooltip />
                    </Box>
                  }
                  source="mask"
                />
                <TextField emptyText={EMPTY_FIELD} label="Комментарий" source="comment" />
                <ContextMenu
                  render={(record: ProviderWhiteIpAddress) => [
                    hasResourceAccess(Resources.UserActions) && (
                      <ContextMenuEntityUserActionsButton
                        entityName="provider_white_ip_address"
                        key="user-action-button"
                      />
                    ),
                    <ContextMenuShowDialogButton
                      key="show-button"
                      onClick={() => {
                        handleOpenShowDialog(record);
                      }}
                    />,
                    hasResourcePermissions('provider_white_ip_addresses', [
                      Permission.Full,
                      Permission.Update,
                    ]) && (
                      <ContextMenuEditDialogButton
                        key="edit-button"
                        onClick={() => {
                          handleOpenEditDialog(record);
                        }}
                      />
                    ),
                    hasResourcePermissions('provider_white_ip_addresses', [
                      Permission.Full,
                      Permission.Delete,
                    ]) && (
                      <ContextMenuDeleteButton
                        key="delete-button"
                        onClick={() => handleOpenDeleteDialog(record)}
                      />
                    ),
                  ]}
                />
              </DatagridWithStickyActions>
            </>
          );
        }}
      />
      <ProvidersShowWhiteIpAddressesShowDialog
        onClose={handleCloseShowDialog}
        onDelete={() => {
          handleOpenDeleteDialog(recordShowDialog);
          handleCloseShowDialog();
        }}
        onEdit={() => {
          handleOpenEditDialog(recordShowDialog);
          handleCloseShowDialog();
        }}
        open={openShowDialog}
        record={recordShowDialog}
      />
      <ProvidersShowWhiteIpAddressesCreateDialog
        onClose={() => setOpenCreateDialog(false)}
        open={openCreateDialog}
        provider={providerId}
      />
      <ProvidersShowWhiteIpAddressesEditDialog
        onClose={handleCloseEditDialog}
        onDelete={() => {
          handleOpenDeleteDialog(recordEditDialog);
          handleCloseEditDialog();
        }}
        open={openEditDialog}
        provider={providerId}
        record={recordEditDialog}
      />
      <ProvidersShowWhiteIpAddressesDeleteDialog
        onClose={handleCloseDeleteDialog}
        open={openDeleteDialog}
        record={recordDeleteDialog}
      />
    </ListPage>
  );
};
